import { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { UNSAFE_NavigationContext, useLocation, type To } from "react-router-dom";
import Modal from "../Modal";

/**
 * Unsaved-changes guard for edit pages.
 *
 * Lumen renders a declarative <BrowserRouter>, where React Router's useBlocker is not available. The
 * provider below therefore guards the router's own navigator (push/replace/go, used by every Link,
 * navigate() and <Navigate>), the browser's back/forward (popstate) and refresh/close/external
 * navigation (beforeunload) — but only while a page reports unsaved changes.
 *
 * Pages only report their dirty state: `useUnsavedChangesGuard(isDirty)`.
 */

type GuardedNavigator = {
  push: (to: To, ...rest: unknown[]) => void;
  replace: (to: To, ...rest: unknown[]) => void;
  go: (delta: number) => void;
};

interface UnsavedChangesContextValue {
  setDirty: (sourceId: string, dirty: boolean) => void;
  leaveWithoutWarning: (navigateAway: () => void) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue>({
  setDirty: () => undefined,
  leaveWithoutWarning: (navigateAway) => navigateAway(),
});

const historyIndex = (): number | null => {
  const index = window.history.state?.idx;
  return typeof index === "number" ? index : null;
};

const pathnameOf = (to: To): string =>
  typeof to === "string" ? new URL(to, window.location.href).pathname : to.pathname ?? window.location.pathname;

/** Render once inside the router, around the routes. */
export const UnsavedChangesGuardProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const location = useLocation();
  const dirtySourcesRef = useRef(new Set<string>());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const pendingNavigationRef = useRef<(() => void) | null>(null);
  const bypassRef = useRef(false);
  const leavingRef = useRef(false);
  const currentIndexRef = useRef<number | null>(historyIndex());

  useEffect(() => {
    currentIndexRef.current = historyIndex();
    leavingRef.current = false;
  }, [location]);

  const setDirty = useCallback((sourceId: string, dirty: boolean) => {
    const sources = dirtySourcesRef.current;
    if (dirty) sources.add(sourceId);
    else sources.delete(sourceId);
    setHasUnsavedChanges(sources.size > 0);
  }, []);

  const leaveWithoutWarning = useCallback((navigateAway: () => void) => {
    bypassRef.current = true;
    try {
      navigateAway();
    } finally {
      bypassRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      pendingNavigationRef.current = null;
      setShowWarning(false);
      return undefined;
    }

    const history = navigator as unknown as GuardedNavigator;
    const original = { push: history.push, replace: history.replace, go: history.go };
    const guardedPathname = window.location.pathname;
    let allowNextPop = false;
    let ignoreNextPop = false;

    const isGuarding = () => dirtySourcesRef.current.size > 0 && !bypassRef.current && !leavingRef.current;
    const block = (proceed: () => void) => {
      pendingNavigationRef.current = proceed;
      setShowWarning(true);
    };

    history.push = (to, ...rest) => {
      const proceed = () => original.push.call(history, to, ...rest);
      if (isGuarding() && pathnameOf(to) !== guardedPathname) block(proceed);
      else proceed();
    };
    history.replace = (to, ...rest) => {
      const proceed = () => original.replace.call(history, to, ...rest);
      if (isGuarding() && pathnameOf(to) !== guardedPathname) block(proceed);
      else proceed();
    };
    history.go = (delta) => {
      const proceed = () => {
        allowNextPop = true;
        original.go.call(history, delta);
      };
      if (isGuarding()) block(proceed);
      else proceed();
    };

    // Browser back/forward: the URL has already changed. Stop the router from handling it, step back
    // to the edit page and ask first. Runs in the capture phase so it precedes the router's listener.
    const handlePopState = (event: PopStateEvent) => {
      if (ignoreNextPop) {
        ignoreNextPop = false;
        event.stopImmediatePropagation();
        return;
      }
      if (allowNextPop) {
        allowNextPop = false;
        return;
      }
      if (!isGuarding() || window.location.pathname === guardedPathname) return;
      const fromIndex = currentIndexRef.current;
      const toIndex = historyIndex();
      // Without a known history position the step cannot be undone; let the router handle it.
      if (fromIndex === null || toIndex === null || toIndex === fromIndex) return;
      const delta = toIndex - fromIndex;
      event.stopImmediatePropagation();
      ignoreNextPop = true;
      window.history.go(-delta);
      block(() => {
        allowNextPop = true;
        window.history.go(delta);
      });
    };

    // Refresh, closing the tab/window and external URLs. Browsers show their own text.
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isGuarding()) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("popstate", handlePopState, true);
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      history.push = original.push;
      history.replace = original.replace;
      history.go = original.go;
      window.removeEventListener("popstate", handlePopState, true);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges, navigator]);

  const stay = () => {
    pendingNavigationRef.current = null;
    setShowWarning(false);
  };

  const leave = () => {
    const proceed = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    setShowWarning(false);
    leavingRef.current = true;
    proceed?.();
  };

  return (
    <UnsavedChangesContext.Provider value={{ setDirty, leaveWithoutWarning }}>
      {children}
      <Modal
        show={showWarning}
        type="warning"
        title={t("common.unsaved_changes.title", { defaultValue: "Unsaved changes" })}
        message={t("common.unsaved_changes.message", {
          defaultValue: "You have unsaved changes. If you leave this page, your changes will be lost.",
        })}
        onClose={stay}
        footer={
          <div className="flex justify-between gap-2">
            <button
              type="button"
              onClick={stay}
              className="w-1/2 rounded-lg bg-gray-300 px-4 py-2 text-black hover:bg-gray-400 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
            >
              {t("common.unsaved_changes.stay", { defaultValue: "Stay on page" })}
            </button>
            <button
              type="button"
              onClick={leave}
              className="w-1/2 rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
            >
              {t("common.unsaved_changes.leave", { defaultValue: "Leave without saving" })}
            </button>
          </div>
        }
      />
    </UnsavedChangesContext.Provider>
  );
};

/**
 * Warns before leaving the page while `isDirty` is true. `leaveWithoutWarning` runs a navigation that
 * must not be warned about: leaving right after a successful save, or an explicit Cancel.
 */
export const useUnsavedChangesGuard = (isDirty: boolean) => {
  const { setDirty, leaveWithoutWarning } = useContext(UnsavedChangesContext);
  const sourceId = useId();

  useEffect(() => {
    setDirty(sourceId, isDirty);
    return () => setDirty(sourceId, false);
  }, [isDirty, setDirty, sourceId]);

  return { leaveWithoutWarning };
};

/** JSON with sorted object keys, so equal form values always serialize the same way. */
const serializeFormValues = (value: unknown): string =>
  JSON.stringify(value, (_key, current) =>
    current && typeof current === "object" && !Array.isArray(current)
      ? Object.keys(current)
          .sort()
          .reduce<Record<string, unknown>>((sorted, key) => {
            sorted[key] = current[key];
            return sorted;
          }, {})
      : current
  ) ?? "";

/**
 * Dirty state of local form values: dirty while they differ from the last loaded or saved values, so
 * changing a field and putting it back is clean again. Call `markClean` with the values just loaded
 * from or saved to the server.
 */
export const useFormDirtyState = <T,>(values: T) => {
  const [cleanSnapshot, setCleanSnapshot] = useState(() => serializeFormValues(values));
  const isDirty = serializeFormValues(values) !== cleanSnapshot;
  const markClean = useCallback((cleanValues: T) => setCleanSnapshot(serializeFormValues(cleanValues)), []);
  return { isDirty, markClean };
};
